import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Mutex } from 'async-mutex';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FileStorageInjectionToken, FileStorageServiceInterface } from '../../interfaces/file-storage.interface';
import {
    JsonNotificationIdentifierModel,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
} from '../../models/low-level/notification-identifier.model';
import {
    JsonNotificationStatusListModel,
    NotificationStatusListModel,
} from '../../interfaces/notification-status.interface';
import { NotificationCompletedListModel } from '../../models/low-level/notification-completed.model';
import { NotificationStatusModelFromJson } from '../../models/low-level/notification-status.model';
import { LocalStorageCommonService } from '../local-storage/local-storage-common.service';

export const KEY_NOTIFICATION_STATUS = 'statusOfNotifications';
export const KEY_NOTIFICATION_COMPLETED = 'notificationRecentlyCompleted';
const KEY_CANCELLATION_CONFIRMED = 'cancellationConfirmed';
const KEY_CONFIRMATION_STARTED = 'confirmationStarted';

@Injectable({
    providedIn: 'root',
})
export class StorageNotificationStatusService {
    private readonly statusArrayMutex: Mutex;
    private readonly completedNotificationsMutex: Mutex;

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly localStorageCommonService: LocalStorageCommonService,
        @Inject(FileStorageInjectionToken) private readonly fileStorageService: FileStorageServiceInterface,
    ) {
        this.statusArrayMutex = new Mutex();
        this.completedNotificationsMutex = new Mutex();
    }

    public getAllNotificationsStatusModelFromStorage(): Observable<NotificationStatusListModel> {
        return this.fileStorageService
            .loadCompressedData<JsonNotificationStatusListModel>(this.statusArrayMutex, KEY_NOTIFICATION_STATUS)
            .pipe(
                map((notificationStatusList) => {
                    const jsonNotificationStatusModelList = notificationStatusList.notificationStatusList;
                    return {
                        notificationStatusList: jsonNotificationStatusModelList.map(
                            (jsonNotificationStatusModel) =>
                                new NotificationStatusModelFromJson(jsonNotificationStatusModel),
                        ),
                        lastSavedTimestamp: notificationStatusList.lastSavedTimestamp,
                    };
                }),
                catchError((error) => {
                    this.log.debug(
                        `StorageNotificationStatusService: getAllNotificationsStatusModelFromStorage: ERROR -> `,
                        error,
                    );
                    return of({ notificationStatusList: [], lastSavedTimestamp: 0 });
                }),
            );
    }

    public saveAllNotificationStatusModelToStorage(
        notificationStatusArray: NotificationStatusListModel,
    ): Observable<void> {
        return this.fileStorageService
            .storeCompressedData(
                this.statusArrayMutex,
                KEY_NOTIFICATION_STATUS,
                notificationStatusArray,
                notificationStatusArray.lastSavedTimestamp,
            )
            .pipe(map(() => undefined));
    }

    public loadCompletedNotificationsToStorage(): Observable<NotificationCompletedListModel> {
        return this.fileStorageService
            .loadCompressedData<NotificationCompletedListModel>(
                this.completedNotificationsMutex,
                KEY_NOTIFICATION_COMPLETED,
            )
            .pipe(
                catchError((error) => {
                    this.log.debug(
                        `StorageNotificationStatusService: getAllNotificationsStatusModelFromStorage: ERROR ->`,
                        error,
                    );
                    return of({ completedNotifications: {}, lastSavedTimestamp: 0 });
                }),
            );
    }

    public saveCompletedNotificationsToStorage(
        completedNotifications: NotificationCompletedListModel,
    ): Observable<void> {
        return this.fileStorageService
            .storeCompressedData(
                this.completedNotificationsMutex,
                KEY_NOTIFICATION_COMPLETED,
                completedNotifications,
                completedNotifications.lastSavedTimestamp,
            )
            .pipe(map(() => undefined));
    }

    // Methods to save/remove/get cancellation confirmation information for an inprogress notification
    public saveCancellationConfirmationForInprogress(notificationId: NotificationIdentifierModel): void {
        this.localStorageCommonService.storageSet(KEY_CANCELLATION_CONFIRMED, notificationId);
    }

    public removeCancellationConfirmationForInprogress(): void {
        this.localStorageCommonService.storageRemove(KEY_CANCELLATION_CONFIRMED);
    }

    public getCancellationConfirmationForInprogress(notificationId: NotificationIdentifierModel): boolean {
        const data =
            this.localStorageCommonService.storageGet<JsonNotificationIdentifierModel>(KEY_CANCELLATION_CONFIRMED);
        return !!data && new NotificationIdentifierModelFromJson(data).equalsTo(notificationId);
    }

    public saveConfirmationStarted(notificationId: NotificationIdentifierModel): void {
        const id = KEY_CONFIRMATION_STARTED + '-' + this.localStorageCommonService.makeId(notificationId);
        this.localStorageCommonService.storageSet(id, true);
    }

    public getConfirmationStarted(notificationId: NotificationIdentifierModel): boolean {
        const id = KEY_CONFIRMATION_STARTED + '-' + this.localStorageCommonService.makeId(notificationId);
        return this.localStorageCommonService.storageGet(id) ?? false;
    }

    public removeConfirmationStarted(notificationId: NotificationIdentifierModel): void {
        const id = KEY_CONFIRMATION_STARTED + '-' + this.localStorageCommonService.makeId(notificationId);
        this.localStorageCommonService.storageRemove(id);
    }
}
